<template>
  <div class="overall-stat-left">
    <div class="overall-stat-left-filter">
      <DatePicker
        :areaId="areaId"
        width="230px"
        :needText="false"
        :clearable="false"
        @changeStartAndEndTime="changeStartAndEndTime"
      />
      <DateUnit v-model="unitType" :needText="false" />
    </div>
    <div v-loading="!loading" class="overall-stat-charts-item">
      <EchartBox
        routeName="OverallStat"
        v-if="loading"
        echartType="每日活跃学生（学生总数、校本、社团）"
        :areaId="areaId"
        :regionId="regionId"
        :getStatFunc="getStudentStat"
        ref="student-stat"
        needFilterType="none"
      />
    </div>
    <div v-loading="!loading" class="overall-stat-charts-item second-line">
      <EchartBox
        routeName="OverallStat"
        v-if="loading"
        echartType="学校、校本、社团增长趋势（院校数）"
        :areaId="areaId"
        :regionId="regionId"
        :getStatFunc="getSchoolStat"
        ref="school-stat"
        needFilterType="none"
        :boxHeight="420"
      />
    </div>
    <div v-loading="!loading" class="overall-stat-charts-item three-line">
      <EchartBox
        routeName="OverallStat"
        v-if="loading"
        echartType="激活码使用趋势"
        :areaId="areaId"
        :regionId="regionId"
        :getStatFunc="getActiveCodeStat"
        ref="active-use-stat"
        needFilterType="none"
        :boxHeight="520"
      />
    </div>
  </div>
</template>

<script>
import DatePicker from '../common/DatePicker'
import DateUnit from '../common/DateUnit'
import EchartBox from '../common/EchartBox'
import { mapActions } from 'vuex'
import { formatDimTimeId, getYearWeek } from '@/utils/common'
import moment from 'moment'
import _ from 'lodash'

export default {
  name: 'OverallStatLeft',
  components: {
    DatePicker,
    DateUnit,
    EchartBox,
  },
  props: {
    areaId: [Number, String],
    regionId: [Number, String],
  },
  data() {
    return {
      unitType: 'day',
      loading: true,
      startAndEndTime: [],
    }
  },
  computed: {
    params() {
      return {
        regionId: this.regionId,
        startTime: this.startAndEndTime[0],
        endTime: this.startAndEndTime[1],
        unit: this.unitType,
      }
    },
  },
  watch: {
    async params(params) {
      if (
        params.regionId &&
        params.startTime &&
        params.endTime &&
        params.unit
      ) {
        this.initStat(params)
      }
    },
  },
  methods: {
    ...mapActions({
      getRegionStats: 'newStat/getRegionStats',
    }),
    changeStartAndEndTime(times) {
      this.startAndEndTime = times
    },
    async initStat(params) {
      const stats = await this.getRegionStats(params)
      const xAxisData = []
      const xData = []
      const startTime = moment(params.startTime)
      const endTime = moment(params.endTime)
      if (params.unit === 'day') {
        while (startTime <= endTime) {
          xAxisData.push(startTime.format('YYYY-MM-DD'))
          xData.push(startTime.format('YYYY-MM-DD'))
          startTime.add(1, 'days')
        }
      }
      if (params.unit === 'week') {
        let startWeeks = getYearWeek(startTime)
        const endWeeks = getYearWeek(endTime)
        while (startWeeks < endWeeks) {
          xAxisData.push(startTime.endOf('isoweek').format('YYYY-MM-DD'))
          xData.push(startTime.endOf('isoweek').format('YYYY-MM-DD'))
          startWeeks += 1
          startTime.add(1, 'weeks')
        }
      }
      if (params.unit === 'month') {
        let startMonth = _.toNumber(startTime.format('MM'))
        const endMonth = _.toNumber(endTime.format('MM'))
        while (startMonth < endMonth) {
          xData.push(startTime.endOf('month').format('YYYY-MM-DD'))
          xAxisData.push(`${startMonth}月`)
          startMonth += 1
          startTime.add(1, 'months')
        }
        if (endTime.format('DD') === endTime.endOf('month').format('DD')) {
          xData.push(startTime.endOf('month').format('YYYY-MM-DD'))
          xAxisData.push(`${startMonth}月`)
        }
      }
      this.initCharts({ stats, xAxisData, xData })
    },
    initCharts(data) {
      this.$refs['student-stat'].onGetStatFunc(data)
      this.$refs['school-stat'].onGetStatFunc(data)
      this.$refs['active-use-stat'].onGetStatFunc(data)
    },
    getStudentStat({ stats, xAxisData, xData }) {
      const seriesData = xData.reduce(
        (arr, item) => {
          const curStat = stats.find(
            stat => formatDimTimeId(stat.dimTimeId) === item
          )
          if (curStat) {
            arr[0].data.push(curStat.studentActiveCount || 0)
            arr[1].data.push(curStat.studentCampusActiveCount || 0)
            arr[2].data.push(curStat.student430ActiveCount || 0)
          } else {
            arr[0].data.push(0)
            arr[1].data.push(0)
            arr[2].data.push(0)
          }
          return arr
        },
        [
          {
            name: '学生总数',
            data: [],
            type: 'line',
            symbolSize: 0.01,
            label: {
              show: true,
            },
          },
          {
            name: '校本学生',
            data: [],
            type: 'line',
            symbolSize: 0.01,
            label: {
              show: true,
            },
          },
          {
            name: '社团学生',
            data: [],
            type: 'line',
            symbolSize: 0.01,
            label: {
              show: true,
            },
          },
        ]
      )
      return {
        echartEl: 'student-stat',
        echartOptions: {
          tooltip: {
            trigger: 'axis',
          },
          grid: {
            left: '6%',
            right: '6%',
            bottom: '10%',
            containLabel: true,
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData,
            splitLine: { show: false },
            axisTick: { show: false },
            axisLine: { show: false },
          },
          yAxis: {
            type: 'value',
            minInterval: 1,
            splitLine: {
              lineStyle: {
                type: 'dashed',
              },
            },
            axisTick: { show: false },
            axisLine: { show: false },
          },
          series: seriesData,
        },
      }
    },
    getSchoolStat({ stats, xAxisData, xData }) {
      const seriesData = xData.reduce(
        (arr, item) => {
          const curStat = stats.find(
            stat => formatDimTimeId(stat.dimTimeId) === item
          )
          if (curStat) {
            arr[0].data.push(curStat.activeSchoolIncrement || 0)
            arr[1].data.push(curStat.schoolCampusIncrement || 0)
            arr[2].data.push(curStat.school430Increment || 0)
          } else {
            arr[0].data.push(0)
            arr[1].data.push(0)
            arr[2].data.push(0)
          }
          return arr
        },
        [
          {
            name: '学校',
            data: [],
            type: 'line',
            symbol: 'none',
            areaStyle: {},
          },
          {
            name: '校本',
            data: [],
            type: 'line',
            symbol: 'none',
            areaStyle: {},
          },
          {
            name: '社团',
            data: [],
            type: 'line',
            symbol: 'none',
            areaStyle: {},
          },
        ]
      )
      return {
        echartEl: 'school-stat',
        echartOptions: {
          tooltip: {
            trigger: 'axis',
          },
          grid: {
            left: '6%',
            right: '6%',
            bottom: '10%',
            containLabel: true,
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData,
            splitLine: { show: false },
            axisTick: { show: false },
            axisLine: { show: false },
          },
          yAxis: {
            type: 'value',
            splitLine: {
              lineStyle: {
                type: 'dashed',
              },
            },
            minInterval: 1,
            axisTick: { show: false },
            axisLine: { show: false },
          },
          series: seriesData,
        },
      }
    },
    getActiveCodeStat({ stats, xAxisData, xData }) {
      const legendData = ['校本激活码', '社团激活码', '机构激活码', '社团占比']
      const seriesData = [
        {
          name: '校本激活码',
          type: 'bar',
          stack: 'total',
          data: [],
        },
        {
          name: '社团激活码',
          type: 'bar',
          stack: 'total',
          data: [],
        },
        {
          name: '机构激活码',
          type: 'bar',
          stack: 'total',
          data: [],
        },
        {
          name: '社团占比',
          type: 'line',
          yAxisIndex: 1,
          data: [],
        },
      ]
      xData.forEach(item => {
        const curStat = stats.find(
          stat => formatDimTimeId(stat.dimTimeId) === item
        )
        if (curStat) {
          const campusActivateCodeTotal =
            curStat.freeCampusActivateCodeTotal || 0
          const activateCodeTotal430 = curStat.pay430ActivateCodeTotal || 0
          const orgActivateCodeTotal = curStat.payOrgActivateCodeTotal || 0
          const sum =
            campusActivateCodeTotal +
            activateCodeTotal430 +
            orgActivateCodeTotal
          let proportion430 = 0
          if (sum > 0) {
            proportion430 = Math.floor((activateCodeTotal430 / sum) * 100)
          }
          seriesData[0].data.push(campusActivateCodeTotal)
          seriesData[1].data.push(activateCodeTotal430)
          seriesData[2].data.push(orgActivateCodeTotal)
          seriesData[3].data.push(proportion430)
        } else {
          seriesData[0].data.push(0)
          seriesData[1].data.push(0)
          seriesData[2].data.push(0)
          seriesData[3].data.push(0)
        }
      })
      return {
        echartEl: 'active-use-stat',
        hasStat: xAxisData.length > 0,
        echartOptions: {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow',
            },
          },
          legend: {
            data: legendData,
            bottom: '6%',
          },
          grid: {
            left: '6%',
            right: '6%',
            bottom: '16%',
            containLabel: true,
          },
          xAxis: {
            type: 'category',
            data: xAxisData,
            axisTick: {
              alignWithLabel: true,
            },
          },
          yAxis: [
            {
              type: 'value',
              axisLine: { show: false },
              axisTick: { show: false },
              minInterval: 1,
              splitLine: {
                lineStyle: {
                  type: 'dashed',
                },
              },
            },
            {
              type: 'value',
              axisLine: { show: false },
              axisTick: { show: false },
              minInterval: 1,
              axisLabel: {
                formatter: '{value}%',
              },
              splitLine: {
                lineStyle: {
                  type: 'dashed',
                },
              },
            },
          ],
          series: seriesData,
        },
      }
    },
  },
}
</script>

<style lang='scss'>
.overall-stat-left {
  .echart-box {
    box-shadow: 0px 0px 0px 0px rgba(92, 92, 93, 0.1);
  }
  .overall-stat-left-filter {
    display: flex;
    justify-content: space-between;
  }
  .overall-stat-charts-item {
    width: 100%;
    height: 500px;
  }
  .second-line {
    height: 420px;
  }
  .three-line {
    height: 520px;
  }
}
</style>